import React, { useEffect, useState } from "react"
import moment from "moment"
import { toast } from "react-toastify"
import { Draggable } from "react-beautiful-dnd"
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap"

import { MathOnlineActivityCard } from "../../../modules/session/activity/math-online-activity-card"
import { EnglishOnlineActivityCard } from "../../../modules/session/activity/english-online-activity-card"
import { GeneralActivityCard } from "../../../modules/session/activity/general-activity-card"
import { GameMetadata } from "../../../modules/games/game"
import { gamesLookupService } from "../../../modules/games/games-lookup"
import {
  ActivityType,
  MY_LIBRARY_ACTIVITY_TYPES,
} from "../../../modules/lesson/activity/activity.types"
import { LllMetadata } from "../../../modules/lll/lll-nodes"
import { lllLookupService } from "../../../modules/lll/lll-lookup"
import { LllNextActivitySearcher } from "../../../modules/lll/lll-next-activity-searcher"
import { LllNodeBranch } from "../../../modules/lll/lll-node-branch"

import awicon from "../../../assets/images/services-icon/coin.png"
import reel from "../../../assets/images/services-icon/reel.png"

import { usePlanLessonLocal } from "./PlanLessonContext"
import {
  getCheckboxProps,
  getQuestionBadgeClass,
  isSessionCompleted,
} from "./PlanLessonHelpers"
import type {
  BasePlanningCardProps,
  DraggableCardProps,
  SessionOnlyCardProps,
} from "./PlanLessonTypes"

const MathOnlineActivityCardAny = MathOnlineActivityCard as any
const EnglishOnlineActivityCardAny = EnglishOnlineActivityCard as any
const GeneralActivityCardAny = GeneralActivityCard as any

type PlanLessonLocalController = ReturnType<typeof usePlanLessonLocal>

interface SharedCardActionParams {
  isHomework: boolean
  isPlanningSession: boolean
  activity: any
  sessionData: any
  copyToHomework: () => unknown
  restrictDeleteToTutorOwner?: boolean
}

interface PlanningActionMenuItem {
  key: string
  label: string
  onClick: () => unknown
  icon?: string
}

interface PlanningCompactActivityCardProps extends BasePlanningCardProps {
  typeLabel: string
  sharedActions: ReturnType<typeof createSharedCardActions>
  assessmentActions: ReturnType<typeof createAssessmentActions>
  editAction?: {
    enabled: boolean
    onActivate: () => unknown
  }
  manualMarkingRequired?: boolean
  highlightClassOverride?: string
}

interface SessionCompactActivityCardProps extends BasePlanningCardProps {
  typeLabel: string
  sharedActions: ReturnType<typeof createSharedCardActions>
  assessmentActions?: ReturnType<typeof createAssessmentActions>
  markAction?: {
    enabled: boolean
    onActivate: () => unknown
  }
  manualMarkingRequired?: boolean
  highlightClassOverride?: string
}

interface PlanningCompactMetaCardProps {
  activity: any
  sessionData: any
  index: number
  enrolment?: any
  isPlanningSession: boolean
  title: string
  typeLabel: string
  coinTotal: number
  coinEarned?: number
  thumbnailUrl?: string | null
  primaryAction?: {
    label: React.ReactNode
    onClick: () => unknown
    tone: "preview" | "delete" | "secondary"
    disabled?: boolean
  } | null
  deleteAction?: (() => unknown) | null
  menuItems?: PlanningActionMenuItem[]
  subtitle?: string
}

interface SessionCompactMetaCardProps {
  activity: any
  sessionData: any
  index: number
  enrolment?: any
  isPlanningSession: boolean
  title: string
  typeLabel: string
  coinTotal: number
  coinEarned?: number
  thumbnailUrl?: string | null
  rolloverActionsEnabled?: boolean
  onRolloverToNextLesson?: (() => unknown) | null
  onRolloverToHomework?: (() => unknown) | null
  onRolloverToNextSession?: (() => unknown) | null
  menuItems?: PlanningActionMenuItem[]
  showNextAvailable?: boolean
  subtitle?: string
}

const COMPACT_CARD_ACTION_WIDTH = 136
const SESSION_CARD_ACTION_WIDTH = 168

function runAsyncAction(action: () => unknown): Promise<void> {
  return Promise.resolve(action()).then(() => undefined)
}

function parseNumericValue(value: unknown): number {
  const parsedValue = parseInt(String(value ?? 0), 10)
  return Number.isNaN(parsedValue) ? 0 : parsedValue
}

function toArray<T = any>(value: any): T[] {
  return Array.isArray(value) ? value : []
}

function getActivityType(activity: any): number {
  return Number(activity?.activity_type)
}

function getDraggableActivityId(activity: any): string {
  return String(
    activity?.pk_lesson_activity_key ?? activity?.pk_lesson_activity_id
  )
}

function getBaseCardProps(
  controller: PlanLessonLocalController,
  {
    isPlanningSession,
    sessionData,
    activity,
    enrolment,
    index,
  }: BasePlanningCardProps
) {
  return {
    activity: activity as any,
    sessionData: sessionData as any,
    index,
    checkbox: getCheckboxProps({
      controller,
      isPlanningSession,
      sessionData,
      activity,
      enrolment,
    }),
  }
}

function createCopyToHomeworkHandler(
  controller: PlanLessonLocalController,
  activity: any,
  isPlanningSession: boolean
): () => unknown {
  if (isPlanningSession) {
    return () =>
      controller.onActivityToHomeLesson(activity.pk_lesson_activity_key, 1)
  }

  return () => controller.onMoveHomeworkActivityPopup(activity as any)
}

function resetNextActivitySelection(
  controller: PlanLessonLocalController,
  activity: any
): void {
  controller.setNextActivity(true)
  controller.setExistingValue([])
  localStorage.removeItem("selectedData")
  controller.setCurrentActivityData(activity)
}

function createAssessmentAddNextHandler(
  controller: PlanLessonLocalController,
  activity: any,
  toHomework: boolean
): () => void {
  return () => {
    resetNextActivitySelection(controller, activity)

    void controller.fetchNextAssessmentActivityData(
      activity.fk_sub_id,
      activity.pk_lesson_activity_key,
      activity.marked_for_homework,
      toHomework ? 1 : 0
    )
  }
}

function createAssessmentViewHandler(
  controller: PlanLessonLocalController,
  activity: any
): () => void {
  return () => {
    controller.tog_Assessment()

    void controller.fetchNextAssessmentActivityData(
      activity.fk_sub_id,
      activity.pk_lesson_activity_key,
      activity.marked_for_homework,
      Number(activity.marked_for_homework)
    )
  }
}

function createSharedCardActions(
  controller: PlanLessonLocalController,
  {
    isHomework,
    isPlanningSession,
    activity,
    sessionData,
    copyToHomework,
    restrictDeleteToTutorOwner = false,
  }: SharedCardActionParams
) {
  const deleteEnabled = restrictDeleteToTutorOwner
    ? controller.sessionStudentData?.session?.fk_tut_key === activity.fk_tut_key
    : true

  return {
    moveActivity: {
      enabled: !isPlanningSession && !!sessionData?.session,
      onActivate: () => controller.onMoveActivityPopup(activity as any),
    },
    rolloverToNextLesson: {
      enabled: !isPlanningSession && !!sessionData?.session,
      onActivate: () =>
        controller.onRolloverActivityToNextSession(activity as any, 0),
    },
    rolloverToHomework: {
      enabled: !isPlanningSession && !!sessionData?.session,
      onActivate: () =>
        controller.onRolloverActivityToNextSession(activity as any, 1),
    },
    copyToHomework: {
      enabled: !isHomework && !!sessionData?.session,
      onActivate: copyToHomework,
    },
    previewActivity: {
      enabled: true,
      onActivate: () => controller.onPreviewActivity(activity as any),
    },
    addNewTask: {
      enabled: isPlanningSession,
      onActivate: () => {
        controller.tog_large()
        controller.setnewactivityhomework(isHomework)
      },
    },
    addComment: {
      enabled: isPlanningSession,
      onActivate: () => controller.handle_commend(activity as any),
    },
    copyToLessonPlan: {
      enabled: isPlanningSession && isHomework,
      onActivate: () =>
        controller.onActivityToHomeLesson(activity.pk_lesson_activity_key, 0),
    },
    moveToHomework: {
      enabled: isPlanningSession && !isHomework,
      onActivate: () =>
        controller.onActivityToHomework(activity.pk_lesson_activity_key, 1),
    },
    moveToLessonPlan: {
      enabled: isPlanningSession && isHomework,
      onActivate: () =>
        controller.onActivityToHomework(activity.pk_lesson_activity_key, 0),
    },
    deleteActivity: {
      enabled: deleteEnabled,
      onActivate: () =>
        controller.onDelectActivityPopup(activity.pk_lesson_activity_key),
    },
  }
}

function createAssessmentActions(
  controller: PlanLessonLocalController,
  activity: any,
  isPlanningSession: boolean
) {
  const activityType = getActivityType(activity)

  return {
    addNextActivity: {
      enabled: !isPlanningSession && activityType === 0,
      onActivate: createAssessmentAddNextHandler(controller, activity, false),
    },
    addNextActivityToHomework: {
      enabled: !isPlanningSession && activityType === 0,
      onActivate: createAssessmentAddNextHandler(controller, activity, true),
    },
    viewActivity: {
      enabled: isPlanningSession && activityType === 0,
      onActivate: createAssessmentViewHandler(controller, activity),
    },
  }
}

function createGeneralEditHandler(
  controller: PlanLessonLocalController,
  activity: any,
  isHomework: boolean
): () => void {
  return () => {
    const activityType = getActivityType(activity)

    if (MY_LIBRARY_ACTIVITY_TYPES.includes(activityType)) {
      controller.handleRowEditActivityClick(activity as any)
      controller.tog_largeEdit()
      return
    }

    if (ActivityType.ONETIME === activityType) {
      void controller.onAddOrEditOnetimeTask({
        isHomework,
        isCreateMode: false,
        activity: activity as any,
      })
    }
  }
}

function createLllSelectedActivity(
  activity: any,
  nextActivityNode: any,
  isHomework: boolean
) {
  const branch = LllNodeBranch.createTo(nextActivityNode)

  return {
    activity_type: ActivityType.LLL,
    is_homework: isHomework ? 1 : 0,
    name: branch.description,
    subject_id: activity.fk_sub_id,
    topicID: 0,
    topic_name: "LLL",
    tutor_comments: "",
    type: "LLL",
    coin: LllMetadata.coinsPerActivity,
    activity_ref_id: nextActivityNode.fqn,
  }
}

function getCardClassName(
  controller: PlanLessonLocalController,
  activityIndex: number,
  isHomework: boolean
): string {
  if (isHomework) {
    return controller.ActivityNavHomeSeleceted === activityIndex
      ? "active card-home-activity"
      : "card-home-activity"
  }

  return controller.ActivityNavSeleceted === activityIndex
    ? "active card-activity"
    : "card-activity"
}

function getStatusMeta(status: unknown): {
  className: string
  label: string | null
} {
  const numericStatus = Number(status)

  if (numericStatus === 0) {
    return {
      className: "text-danger",
      label: "Incomplete",
    }
  }

  if (numericStatus === 1) {
    return {
      className: "text-success",
      label: "Completed",
    }
  }

  return {
    className: "",
    label: null,
  }
}

function getRolledOverText(rollDate: unknown): string | null {
  if (!rollDate) return null
  const parsed = moment(rollDate as any, moment.ISO_8601)
  if (!parsed.isValid()) return null
  return `Rolled over from ${parsed.format("DD/MMM/YYYY")}`
}

function getHighlightClass(
  sessionData: any,
  activity: any,
  options: {
    isGeneral?: boolean
    activityFlowCompleted?: boolean
    isPlatform?: boolean
  } = {}
): string {
  if (sessionData?.session?.ses_is_completed !== 1) {
    return ""
  }

  const status = Number(activity?.status)
  const hasMastryScore =
    activity?.mastryScore != null && activity?.mastryScore !== ""

  if (status === 1 && hasMastryScore) {
    const score = parseNumericValue(activity?.mastryScore)
    if (score > 75) {
      return "activity-card-success"
    }
    if (score < 75) {
      return "activity-card-blue"
    }
    // score === 75 falls through to danger (matching original behavior)
  } else if (options.isGeneral) {
    if (options.activityFlowCompleted) {
      if (options.isPlatform) return "activity-card-blue"
      return "activity-card-success"
    }
  } else if (status === 1) {
    return "activity-card-blue"
  }

  return "activity-card-danger"
}

function getQuestionItemsForActivityQuestion(
  activity: any,
  activityQuestion: any
): any[] {
  if (Number(activity?.fk_sub_id) === 1) {
    return toArray(activityQuestion?.english_question)
  }

  return toArray(activityQuestion?.question)
}

function getAttemptsFromQuestion(question: any): any[] {
  const directAttempts = toArray(
    question?.StudentAnsAttempts ??
      question?.studentAnsAttempts ??
      question?.student_ans_attempts
  )

  if (directAttempts.length > 0) {
    return directAttempts
  }

  const questionParts = toArray(
    question?.question_parts ?? question?.english_question_parts
  )

  return questionParts.flatMap(part =>
    toArray(
      part?.StudentAnsAttempts ??
        part?.studentAnsAttempts ??
        part?.student_ans_attempts
    )
  )
}

function getQuestionAttemptStatus(question: any): number | null {
  const attempts = getAttemptsFromQuestion(question)

  if (attempts.length === 0) {
    return null
  }

  let color = ""

  attempts.forEach(answerAttemptQuestion => {
    color += String(answerAttemptQuestion?.points_obtained ?? "")
  })

  const colorInt = parseInt(color, 10)
  return Number.isNaN(colorInt) ? null : colorInt
}

function getAssessmentQuestionStats(activity: any): {
  totalCoins: number
  totalQuestions: number
  attemptedCount: number
  questionStates: Array<number | null>
} {
  let totalCoins = 0
  let totalQuestions = 0
  let attemptedCount = 0
  const questionStates: Array<number | null> = []

  toArray(activity?.LessonActivitiesQuestions).forEach(activityQuestion => {
    const questions = getQuestionItemsForActivityQuestion(
      activity,
      activityQuestion
    )

    questions.forEach(question => {
      totalQuestions += 1
      totalCoins += 2

      const status = getQuestionAttemptStatus(question)

      if (status !== null) {
        attemptedCount += 1
      }

      questionStates.push(status)
    })
  })

  return {
    totalCoins,
    totalQuestions,
    attemptedCount,
    questionStates,
  }
}

function renderQuestionBadgeContent(
  score: number | null,
  index: number
): React.ReactNode {
  if (score === 1 || score === 2) {
    return <i className="fas fa-check"></i>
  }

  if (score === 0) {
    return <i className="mdi mdi-window-close"></i>
  }

  return index + 1
}

function getPrimaryActivityNode(activity: any): any | null {
  const activityNodes = toArray(activity?.activity_node)
  return activityNodes.length > 0 ? activityNodes[0] : null
}

function getText(value: unknown): string {
  if (value == null) {
    return ""
  }

  return String(value).trim()
}

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    const text = getText(value)
    if (text !== "") {
      return text
    }
  }

  return ""
}

function collectTopicNames(value: any, topicNames: string[]): void {
  if (Array.isArray(value)) {
    value.forEach(item => collectTopicNames(item, topicNames))
    return
  }

  if (!value || typeof value !== "object") {
    return
  }

  const directTopicName = getText(value.topic_name)
  if (directTopicName !== "") {
    topicNames.push(directTopicName)
  }

  const masterTopicName = getText(value?.activity_master_topic?.topic_name)
  if (masterTopicName !== "") {
    topicNames.push(masterTopicName)
  }

  Object.keys(value).forEach(key => {
    if (key === "topic_name" || key === "activity_master_topic") {
      return
    }

    collectTopicNames(value[key], topicNames)
  })
}

function getTopicsText(activity: any): string {
  const collectedTopicNames: string[] = []
  collectTopicNames(activity?.topics, collectedTopicNames)

  if (collectedTopicNames.length > 0) {
    return [...new Set(collectedTopicNames.filter(Boolean))].join(", ")
  }

  const activityNode = getPrimaryActivityNode(activity)

  return firstNonEmpty(
    activity?.topic_name,
    activityNode?.topic_name,
    activityNode?.activity_master_topic?.topic_name
  )
}

function getActivityNameOnly(activity: any): string {
  const activityType = getActivityType(activity)
  const activityNode = getPrimaryActivityNode(activity)

  if (activityType === 0) {
    return firstNonEmpty(
      activityNode?.node_name,
      activityNode?.name,
      activity?.activity_name,
      activity?.node_name,
      activity?.title,
      activity?.name,
      activity?.custom_title
    )
  }

  if (activityType === 8) {
    return firstNonEmpty(
      activityNode?.name,
      activityNode?.node_name,
      activity?.activity_name,
      activity?.node_name,
      activity?.title,
      activity?.name,
      activity?.custom_title
    )
  }

  if (activityType === 2) {
    return firstNonEmpty(
      activityNode?.topic_name,
      activity?.name,
      activity?.activity_name,
      activity?.title,
      activity?.custom_title
    )
  }

  if (activityType === 6) {
    return firstNonEmpty(
      activityNode?.video_name,
      activityNode?.name,
      activity?.activity_name,
      activity?.title,
      activity?.name,
      activity?.custom_title
    )
  }

  if (activityType === 7) {
    return firstNonEmpty(
      activityNode?.name,
      activityNode?.node_name,
      activityNode?.video_name,
      activity?.activity_name,
      activity?.title,
      activity?.name,
      activity?.custom_title,
      activityNode?.file_name
    )
  }

  if (
    activityType === 1 ||
    activityType === 3 ||
    activityType === 4 ||
    activityType === 5 ||
    activityType === 10 ||
    activityType === ActivityType.ONETIME
  ) {
    return firstNonEmpty(
      activityNode?.name,
      activityNode?.node_name,
      activity?.activity_name,
      activity?.title,
      activity?.name,
      activity?.custom_title
    )
  }

  return firstNonEmpty(
    activityNode?.node_name,
    activityNode?.name,
    activityNode?.topic_name,
    activityNode?.video_name,
    activityNode?.file_name,
    activity?.activity_name,
    activity?.node_name,
    activity?.title,
    activity?.name,
    activity?.custom_title
  )
}

function getLevelText(activity: any): string {
  const activityNode = getPrimaryActivityNode(activity)

  return firstNonEmpty(activity?.level_name, activityNode?.level_name)
}

function joinTitleParts(parts: string[]): string {
  const cleanedParts = parts
    .map(part => getText(part))
    .filter(Boolean)
    .filter((part, index, array) => array.indexOf(part) === index)

  return cleanedParts.join(" - ")
}

function getActivityTitle(activity: any): string {
  const levelText = getLevelText(activity)
  const topicsText = getTopicsText(activity)
  const activityName = getActivityNameOnly(activity)

  return (
    joinTitleParts([levelText, topicsText, activityName]) ||
    firstNonEmpty(activity?.name, activity?.activity_name, activity?.title, "-")
  )
}

function getCoinTotal(activity: any, derivedTotalCoins: number): number {
  const configuredCoin = parseNumericValue(activity?.coin)
  if (configuredCoin > 0) {
    return configuredCoin
  }

  const configuredTotalScore = parseNumericValue(activity?.totalScore)
  if (configuredTotalScore > 0) {
    return configuredTotalScore
  }

  const activityNode = getPrimaryActivityNode(activity)
  const activityNodeCoins = parseNumericValue(
    activityNode?.coins ?? activityNode?.coin
  )
  if (activityNodeCoins > 0) {
    return activityNodeCoins
  }

  const activityNodeTotalScore = parseNumericValue(activityNode?.totalScore)
  if (activityNodeTotalScore > 0) {
    return activityNodeTotalScore
  }

  const activityType = getActivityType(activity)

  if (activityType === ActivityType.PLATFORM_VIDEO) {
    return 2
  }

  if (activityType === ActivityType.PLATFORM_WORKSHEET) {
    return 20
  }

  if (activityType === ActivityType.LLL) {
    return LllMetadata.coinsPerActivity
  }

  if (activityType === ActivityType.PLATFORM_GAMES) {
    return GameMetadata.coinsPerGame
  }

  return derivedTotalCoins
}

function getCoinEarned(activity: any): number {
  return parseNumericValue(activity?.coins_earned)
}

function getAttemptedText(
  attemptedCount: number,
  totalQuestions: number
): string | null {
  if (totalQuestions <= 0) {
    return null
  }

  return `${attemptedCount}/${totalQuestions} attempted`
}

function getScoreText(sessionData: any, activity: any): string | null {
  if (sessionData?.session?.ses_is_completed !== 1) {
    return null
  }

  const score = activity?.mastryScore
  return score != null && score !== "" ? `Score: ${score}` : "Score: 0"
}

function getActionButtonStyle(
  tone: "preview" | "delete" | "secondary" | "menu"
): React.CSSProperties {
  const baseStyle: React.CSSProperties = {
    color: "#1b1b1b",
    border: "none",
    minHeight: "24px",
    padding: "4px 6px",
    lineHeight: 1.05,
    borderRadius: "4px",
  }

  if (tone === "preview") {
    return {
      ...baseStyle,
      backgroundColor: "#c9d7f2",
    }
  }

  if (tone === "delete") {
    return {
      ...baseStyle,
      backgroundColor: "#ebc9aa",
    }
  }

  if (tone === "menu") {
    return {
      ...baseStyle,
      backgroundColor: "#d8deec",
    }
  }

  return {
    ...baseStyle,
    backgroundColor: "#d8deec",
  }
}

const PlanningCardActionButton = ({
  label,
  onClick,
  tone,
  fontSize = 9,
  disabled = false,
  title,
}: {
  label: React.ReactNode
  onClick: () => unknown
  tone: "preview" | "delete" | "secondary"
  fontSize?: number
  disabled?: boolean
  title?: string
}) => {
  return (
    <button
      type="button"
      className="btn btn-sm w-100 fw-700"
      title={title}
      style={{
        ...getActionButtonStyle(tone),
        fontSize: `${fontSize}px`,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        whiteSpace: "nowrap",
        paddingLeft: "2px",
        paddingRight: "2px",
        overflow: "visible",
        lineHeight: 1.1,
      }}
      disabled={disabled}
      onMouseDown={event => event.stopPropagation()}
      onClick={event => {
        event.stopPropagation()
        if (disabled) {
          return
        }
        void onClick()
      }}
    >
      {label}
    </button>
  )
}

const EmptyPlanningActionCell = () => <div style={{ minHeight: "24px" }} />

const PlanningCardActionMenu = ({
  items,
  fontSize = 9,
}: {
  items: PlanningActionMenuItem[]
  fontSize?: number
}) => {
  const [open, setOpen] = useState(false)

  return (
    <Dropdown
      isOpen={open}
      toggle={() => setOpen(prev => !prev)}
      onMouseDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
    >
      <DropdownToggle
        caret={false}
        className="btn btn-sm w-100 fw-700"
        style={{
          ...getActionButtonStyle("menu"),
          fontSize: `${fontSize}px`,
        }}
      >
        <i className="mdi mdi-dots-horizontal"></i>
      </DropdownToggle>

      <DropdownMenu className="custom-position">
        {items.map(item => (
          <DropdownItem
            key={item.key}
            onClick={event => {
              event.stopPropagation()
              void item.onClick()
            }}
          >
            {item.icon ? (
              <i className={`${item.icon} font-size-15 me-1`}></i>
            ) : null}
            {item.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  )
}

function buildPlanningMenuItems({
  sharedActions,
  assessmentActions,
  editAction,
}: {
  sharedActions: ReturnType<typeof createSharedCardActions>
  assessmentActions: ReturnType<typeof createAssessmentActions>
  editAction?: {
    enabled: boolean
    onActivate: () => unknown
  }
}): PlanningActionMenuItem[] {
  const items: PlanningActionMenuItem[] = []

  if (assessmentActions.viewActivity.enabled) {
    items.push({
      key: "viewActivity",
      label: "View Activity",
      icon: "mdi mdi-eye-outline",
      onClick: assessmentActions.viewActivity.onActivate,
    })
  }

  if (sharedActions.addComment.enabled) {
    items.push({
      key: "addComment",
      label: "Add Comment",
      icon: "mdi mdi-comment-outline",
      onClick: sharedActions.addComment.onActivate,
    })
  }

  if (editAction?.enabled) {
    items.push({
      key: "edit",
      label: "Edit",
      icon: "mdi mdi-pencil",
      onClick: editAction.onActivate,
    })
  }

  return items
}

function buildSessionMenuItems(
  sharedActions: ReturnType<typeof createSharedCardActions>,
  assessmentActions?: ReturnType<typeof createAssessmentActions>,
  markAction?: { enabled: boolean; onActivate: () => unknown }
): PlanningActionMenuItem[] {
  const items: PlanningActionMenuItem[] = []

  if (sharedActions.moveActivity.enabled) {
    items.push({
      key: "rollOverToNextSession",
      label: "Roll over to next session",
      icon: "mdi mdi-skip-forward",
      onClick: sharedActions.moveActivity.onActivate,
    })
  }

  if (markAction?.enabled) {
    items.push({
      key: "mark",
      label: "Mark",
      icon: "mdi mdi-checkbox-marked-circle-outline",
      onClick: markAction.onActivate,
    })
  }

  if (sharedActions.previewActivity.enabled) {
    items.push({
      key: "preview",
      label: "Review",
      icon: "mdi mdi-eye-outline",
      onClick: sharedActions.previewActivity.onActivate,
    })
  }

  if (sharedActions.copyToHomework.enabled) {
    items.push({
      key: "copyToHomework",
      label: "Copy to homework",
      icon: "mdi mdi-home-edit-outline",
      onClick: sharedActions.copyToHomework.onActivate,
    })
  }

  if (assessmentActions?.addNextActivity?.enabled) {
    items.push({
      key: "addNextActivity",
      label: "Add the next activity",
      icon: "mdi mdi-skip-next",
      onClick: assessmentActions.addNextActivity.onActivate,
    })
  }

  if (assessmentActions?.addNextActivityToHomework?.enabled) {
    items.push({
      key: "addNextActivityToHomework",
      label: "Add the next activity to homework",
      icon: "mdi mdi-skip-next",
      onClick: assessmentActions.addNextActivityToHomework.onActivate,
    })
  }

  if (sharedActions.deleteActivity.enabled) {
    items.push({
      key: "delete",
      label: "Delete",
      icon: "far fa-trash-alt",
      onClick: sharedActions.deleteActivity.onActivate,
    })
  }

  return items
}

function getPlanningMoveAction(
  sharedActions: ReturnType<typeof createSharedCardActions>,
  isHomework: boolean
) {
  if (isHomework) {
    return {
      enabled: sharedActions.moveToLessonPlan.enabled,
      label: "Move to lesson",
      onActivate: sharedActions.moveToLessonPlan.onActivate,
    }
  }

  return {
    enabled: sharedActions.moveToHomework.enabled,
    label: "Move to h/w",
    onActivate: sharedActions.moveToHomework.onActivate,
  }
}

function getPlanningCopyAction(
  sharedActions: ReturnType<typeof createSharedCardActions>,
  isHomework: boolean
) {
  if (isHomework) {
    return {
      enabled: sharedActions.copyToLessonPlan.enabled,
      label: "Copy to lesson",
      onActivate: sharedActions.copyToLessonPlan.onActivate,
    }
  }

  return {
    enabled: sharedActions.copyToHomework.enabled,
    label: "Copy to h/w",
    onActivate: sharedActions.copyToHomework.onActivate,
  }
}

function getGameFallbackTitle(activity: any): string {
  return firstNonEmpty(
    activity?.activity_node?.[0]?.name,
    activity?.activity_node?.[0]?.node_name,
    activity?.name,
    activity?.activity_name,
    activity?.title,
    activity?.custom_title,
    "Game"
  )
}

function getLllFallbackTitle(activity: any): string {
  return firstNonEmpty(
    activity?.name,
    activity?.activity_name,
    activity?.title,
    activity?.topic_name,
    activity?.custom_title,
    "LLL"
  )
}

function useResolvedGameDetails(activity: any): {
  title: string
  thumbnailUrl: string
  description: string
} {
  const fallbackTitle = getGameFallbackTitle(activity)
  const fallbackThumbnailUrl = firstNonEmpty(
    activity?.activity_node?.[0]?.image,
    activity?.image,
    ""
  )
  const [details, setDetails] = useState({
    title: fallbackTitle,
    thumbnailUrl: fallbackThumbnailUrl,
    description: "",
  })

  useEffect(() => {
    let isActive = true
    setDetails({
      title: fallbackTitle,
      thumbnailUrl: fallbackThumbnailUrl,
      description: "",
    })

    async function loadGameDetails() {
      if (!activity?.fk_activity_node_id) {
        return
      }

      try {
        const allGames = await gamesLookupService.getGames()
        const matchedGame = allGames.find(
          (game: any) => game?.id == activity?.fk_activity_node_id
        )

        if (!isActive) {
          return
        }

        setDetails({
          title: firstNonEmpty(matchedGame?.name, fallbackTitle, "Game"),
          thumbnailUrl: firstNonEmpty(
            matchedGame?.image,
            fallbackThumbnailUrl,
            ""
          ),
          description: firstNonEmpty(matchedGame?.description, ""),
        })
      } catch (error) {
        if (isActive) {
          setDetails({
            title: fallbackTitle || "Game",
            thumbnailUrl: fallbackThumbnailUrl,
            description: "",
          })
        }
      }
    }

    void loadGameDetails()

    return () => {
      isActive = false
    }
  }, [activity?.fk_activity_node_id, fallbackThumbnailUrl, fallbackTitle])

  return details
}

function useResolvedLllTitle(activity: any): {
  title: string
  subtitle: string
} {
  const fallbackTitle = getLllFallbackTitle(activity)
  const [details, setDetails] = useState({
    title: fallbackTitle,
    subtitle: "",
  })

  useEffect(() => {
    let isActive = true
    setDetails({ title: fallbackTitle, subtitle: "" })

    async function loadLllTitle() {
      if (!activity?.activity_ref_id) {
        return
      }

      try {
        await lllLookupService.getActivityTree()
        const activityNode = lllLookupService.findNodeByFqn(
          activity.activity_ref_id as string
        )

        if (!activityNode || !isActive) {
          return
        }

        const branch = LllNodeBranch.createTo(activityNode)
        const topicDisplay = branch?.topicDisplay || ""
        const activityDisplay = branch?.activityDisplay || ""

        setDetails({
          title: firstNonEmpty(topicDisplay, fallbackTitle, "LLL"),
          subtitle: activityDisplay,
        })
      } catch (error) {
        if (isActive) {
          setDetails({ title: fallbackTitle || "LLL", subtitle: "" })
        }
      }
    }

    void loadLllTitle()

    return () => {
      isActive = false
    }
  }, [activity?.activity_ref_id, fallbackTitle])

  return details
}

const PlanningCompactActivityCard = ({
  isHomework,
  isPlanningSession,
  activity,
  sessionData,
  index,
  enrolment,
  typeLabel,
  sharedActions,
  assessmentActions,
  editAction,
  manualMarkingRequired,
  highlightClassOverride,
}: PlanningCompactActivityCardProps) => {
  const controller = usePlanLessonLocal()

  const checkbox = getCheckboxProps({
    controller,
    isPlanningSession,
    sessionData,
    activity,
    enrolment,
  })

  const { totalCoins, totalQuestions, attemptedCount, questionStates } =
    getAssessmentQuestionStats(activity)

  const statusMeta = getStatusMeta(activity?.status)
  const attemptedText = getAttemptedText(attemptedCount, totalQuestions)
  const scoreText = getScoreText(sessionData, activity)
  const highlightClass =
    highlightClassOverride ?? getHighlightClass(sessionData, activity)
  const coinTotal = getCoinTotal(activity, totalCoins)
  const coinEarned = getCoinEarned(activity)

  const moveAction = getPlanningMoveAction(sharedActions, isHomework)
  const copyAction = getPlanningCopyAction(sharedActions, isHomework)
  const menuItems = buildPlanningMenuItems({
    sharedActions,
    assessmentActions,
    editAction,
  })
  const rolledOverText = getRolledOverText(activity?.roll_date)

  return (
    <div
      className={`${highlightClass} set1 bg-supermild`}
      data-key={activity.pk_lesson_activity_key}
      style={{
        minHeight: "unset",
        padding: "4px 6px",
      }}
    >
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {rolledOverText ? (
            <span
              className="cs-txt-danger me-2"
              style={{ fontSize: "8px", display: "block", lineHeight: 1.1 }}
            >
              {rolledOverText}
            </span>
          ) : null}
          <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
            {statusMeta.label ? (
              <span
                className={`${statusMeta.className}`}
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {statusMeta.label}
              </span>
            ) : null}

            {attemptedText ? (
              <span
                className="text-danger"
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {attemptedText}
              </span>
            ) : null}

            {scoreText ? (
              <span
                className="text-danger"
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {scoreText}
              </span>
            ) : null}

            {activity?.tutor_comments ? (
              <span
                className="text-dark"
                style={{ cursor: "pointer", fontSize: "9px", lineHeight: 1 }}
                title={`Comment: ${activity.tutor_comments}`}
              >
                <i className="fas fa-info-circle"></i>
              </span>
            ) : null}

            {checkbox?.checkable ? (
              <input
                type="checkbox"
                checked={!!checkbox.checked}
                onMouseDown={event => event.stopPropagation()}
                onChange={event => {
                  event.stopPropagation()
                  void checkbox.onCheckedChanged?.()
                }}
              />
            ) : null}
          </div>

          <h6
            className="text-dark fw-700"
            style={{
              fontSize: "10px",
              lineHeight: 1.15,
              marginBottom: "3px",
              overflow: "hidden",
              wordBreak: "break-word",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
            }}
            title={getActivityTitle(activity)}
          >
            {index + 1}) {getActivityTitle(activity)}
          </h6>

          <div
            className="border-top"
            style={{ marginTop: "2px", marginBottom: "3px", opacity: 0.7 }}
          ></div>

          <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
            <div
              className="d-flex align-items-center fw-400 text-dark"
              style={{ fontSize: "9px", lineHeight: 1.05 }}
            >
              <span>
                <img className="me-05" width={12} src={reel} alt="" />
              </span>
              {typeLabel}
            </div>

            <div
              className="d-flex align-items-center fw-400 text-dark"
              style={{ fontSize: "9px", lineHeight: 1.05 }}
            >
              <img className="me-05" width={12} src={awicon} alt="" />
              {coinEarned}/{coinTotal}
            </div>
          </div>

          <div className="question-count mt-0">
            {questionStates.length > 0 ? (
              <ul
                className="list-unstyled mb-0 d-flex align-items-center flex-wrap"
                style={{ gap: "2px" }}
              >
                {questionStates.map((score, badgeIndex) => {
                  const normalizedScore =
                    typeof score === "number" && Number.isFinite(score)
                      ? score
                      : null

                  return (
                    <li
                      key={badgeIndex}
                      className={getQuestionBadgeClass(normalizedScore)}
                      style={{
                        minWidth: "17px",
                        height: "15px",
                        fontSize: "8px",
                        lineHeight: "15px",
                        padding: "0 3px",
                        textAlign: "center",
                        borderRadius: "8px",
                      }}
                    >
                      {renderQuestionBadgeContent(normalizedScore, badgeIndex)}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div style={{ fontSize: "9px" }}>&nbsp;</div>
            )}
          </div>
        </div>

        <div
          style={{
            width: `${COMPACT_CARD_ACTION_WIDTH}px`,
            flex: `0 0 ${COMPACT_CARD_ACTION_WIDTH}px`,
          }}
        >
          <div className="d-grid" style={{ gap: "3px" }}>
            <PlanningCardActionButton
              label="Preview"
              onClick={sharedActions.previewActivity.onActivate}
              tone="preview"
            />

            <div
              className="d-grid"
              style={{
                gridTemplateColumns: "1fr 1fr",
                gap: "3px",
              }}
            >
              <div className="d-grid" style={{ gap: "3px" }}>
                {sharedActions.deleteActivity.enabled ? (
                  <PlanningCardActionButton
                    label="Delete"
                    onClick={sharedActions.deleteActivity.onActivate}
                    tone="delete"
                  />
                ) : (
                  <EmptyPlanningActionCell />
                )}

                <PlanningCardActionMenu items={menuItems} />
              </div>

              <div className="d-grid" style={{ gap: "3px" }}>
                {moveAction.enabled ? (
                  <PlanningCardActionButton
                    label={moveAction.label}
                    onClick={moveAction.onActivate}
                    tone="secondary"
                  />
                ) : (
                  <EmptyPlanningActionCell />
                )}

                {copyAction.enabled ? (
                  <PlanningCardActionButton
                    label={copyAction.label}
                    onClick={copyAction.onActivate}
                    tone="secondary"
                  />
                ) : (
                  <EmptyPlanningActionCell />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {manualMarkingRequired ? (
        <div
          className="manual_mark_div"
          style={{ marginTop: "2px", padding: "2px 4px" }}
        >
          <span className="d-flex align-items-center" style={{ gap: "3px" }}>
            <i
              className="mdi mdi-alert-outline"
              style={{ fontSize: "10px", lineHeight: 1 }}
            ></i>
            <p className="mb-0" style={{ fontSize: "8px", lineHeight: 1 }}>
              Manual marking required
            </p>
          </span>
        </div>
      ) : null}
    </div>
  )
}

const SessionCompactActivityCard = ({
  isHomework,
  isPlanningSession,
  activity,
  sessionData,
  index,
  enrolment,
  typeLabel,
  sharedActions,
  assessmentActions,
  markAction,
  manualMarkingRequired,
  highlightClassOverride,
}: SessionCompactActivityCardProps) => {
  const controller = usePlanLessonLocal()

  const checkbox = getCheckboxProps({
    controller,
    isPlanningSession,
    sessionData,
    activity,
    enrolment,
  })

  const { totalCoins, totalQuestions, attemptedCount, questionStates } =
    getAssessmentQuestionStats(activity)

  const statusMeta = getStatusMeta(activity?.status)
  const attemptedText = getAttemptedText(attemptedCount, totalQuestions)
  const scoreText = getScoreText(sessionData, activity)
  const highlightClass =
    highlightClassOverride ?? getHighlightClass(sessionData, activity)
  const coinTotal = getCoinTotal(activity, totalCoins)
  const coinEarned = getCoinEarned(activity)
  const menuItems = buildSessionMenuItems(
    sharedActions,
    assessmentActions,
    markAction
  )
  const rolledOverText = getRolledOverText(activity?.roll_date)

  return (
    <div
      className={`${highlightClass} set1 bg-supermild`}
      data-key={activity.pk_lesson_activity_key}
      style={{
        minHeight: "unset",
        padding: "4px 6px",
      }}
    >
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {rolledOverText ? (
            <span
              className="cs-txt-danger me-2"
              style={{ fontSize: "8px", display: "block", lineHeight: 1.1 }}
            >
              {rolledOverText}
            </span>
          ) : null}
          <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
            <div
              className="d-flex align-items-center fw-400 text-dark"
              style={{ fontSize: "8px", lineHeight: 1.1 }}
            >
              <span>
                <img className="me-05" width={12} src={reel} alt="" />
              </span>
              {typeLabel}
            </div>

            {statusMeta.label ? (
              <span
                className={`${statusMeta.className}`}
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {statusMeta.label}
              </span>
            ) : null}

            {attemptedText ? (
              <span
                className="text-danger"
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {attemptedText}
              </span>
            ) : null}

            {scoreText ? (
              <span
                className="text-danger"
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {scoreText}
              </span>
            ) : null}

            {activity?.tutor_comments ? (
              <span
                className="text-dark"
                style={{ cursor: "pointer", fontSize: "8px", lineHeight: 1 }}
                title={`Comment: ${activity.tutor_comments}`}
              >
                <i className="fas fa-info-circle"></i>
              </span>
            ) : null}

            {checkbox?.checkable ? (
              <input
                type="checkbox"
                checked={!!checkbox.checked}
                onMouseDown={event => event.stopPropagation()}
                onChange={event => {
                  event.stopPropagation()
                  void checkbox.onCheckedChanged?.()
                }}
              />
            ) : null}
          </div>

          <div className="d-flex justify-content-between align-items-start gap-2">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <h6
                className="text-dark fw-700"
                style={{
                  fontSize: "10px",
                  lineHeight: 1.2,
                  marginBottom: "3px",
                  overflow: "hidden",
                  wordBreak: "break-word",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
                title={getActivityTitle(activity)}
              >
                {index + 1}) {getActivityTitle(activity)}
              </h6>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-1 mb-1">
                <div className="question-count mt-0">
                  {questionStates.length > 0 ? (
                    <ul
                      className="list-unstyled mb-0 d-flex align-items-center flex-wrap"
                      style={{ gap: "2px" }}
                    >
                      {questionStates.map((score, badgeIndex) => {
                        const normalizedScore =
                          typeof score === "number" && Number.isFinite(score)
                            ? score
                            : null

                        return (
                          <li
                            key={badgeIndex}
                            className={getQuestionBadgeClass(normalizedScore)}
                            style={{
                              minWidth: "17px",
                              height: "15px",
                              fontSize: "8px",
                              lineHeight: "15px",
                              padding: "0 3px",
                              textAlign: "center",
                              borderRadius: "8px",
                            }}
                          >
                            {renderQuestionBadgeContent(
                              normalizedScore,
                              badgeIndex
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <div style={{ fontSize: "9px" }}>&nbsp;</div>
                  )}
                </div>

                <div
                  className="d-flex align-items-center fw-400 text-dark"
                  style={{ fontSize: "9px", lineHeight: 1.05 }}
                >
                  <img className="me-05" width={12} src={awicon} alt="" />
                  {coinEarned}/{coinTotal}
                </div>
              </div>
            </div>

            <div
              style={{
                width: `${SESSION_CARD_ACTION_WIDTH}px`,
                flex: `0 0 ${SESSION_CARD_ACTION_WIDTH}px`,
              }}
            >
              <div className="d-grid" style={{ gap: "3px" }}>
                {sharedActions.rolloverToNextLesson.enabled ||
                sharedActions.rolloverToHomework.enabled ? (
                  <div
                    className="d-grid"
                    style={{ gridTemplateColumns: "1fr 1fr", gap: "3px" }}
                  >
                    {sharedActions.rolloverToNextLesson.enabled ? (
                      <PlanningCardActionButton
                        label={
                          <>
                            <i
                              className="mdi mdi-redo"
                              style={{ fontSize: "10px", marginRight: "2px" }}
                              aria-hidden="true"
                            />
                            To lesson
                          </>
                        }
                        onClick={sharedActions.rolloverToNextLesson.onActivate}
                        tone="secondary"
                        fontSize={8}
                        title="Rollover to next Lesson"
                      />
                    ) : (
                      <EmptyPlanningActionCell />
                    )}
                    {sharedActions.rolloverToHomework.enabled ? (
                      <PlanningCardActionButton
                        label={
                          <>
                            <i
                              className="mdi mdi-redo"
                              style={{ fontSize: "10px", marginRight: "2px" }}
                              aria-hidden="true"
                            />
                            To h/w
                          </>
                        }
                        onClick={sharedActions.rolloverToHomework.onActivate}
                        tone="secondary"
                        fontSize={8}
                        title="Rollover to Homework"
                      />
                    ) : (
                      <EmptyPlanningActionCell />
                    )}
                  </div>
                ) : null}

                {menuItems.length > 0 ? (
                  <PlanningCardActionMenu items={menuItems} fontSize={9} />
                ) : (
                  <EmptyPlanningActionCell />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {manualMarkingRequired ? (
        <div
          className="manual_mark_div"
          style={{ marginTop: "2px", padding: "2px 4px" }}
        >
          <span className="d-flex align-items-center" style={{ gap: "3px" }}>
            <i
              className="mdi mdi-alert-outline"
              style={{ fontSize: "10px", lineHeight: 1 }}
            ></i>
            <p className="mb-0" style={{ fontSize: "8px", lineHeight: 1 }}>
              Manual marking required
            </p>
          </span>
        </div>
      ) : null}
    </div>
  )
}

const CompactMetaCardThumbnail = ({
  thumbnailUrl,
  size,
}: {
  thumbnailUrl?: string | null
  size: number
}) => {
  if (!thumbnailUrl) {
    return null
  }

  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        flex: `0 0 ${size}px`,
        borderRadius: "6px",
        overflow: "hidden",
        backgroundColor: "#eef2f7",
      }}
    >
      <img
        src={thumbnailUrl}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </div>
  )
}

const PlanningCompactMetaCard = ({
  activity,
  sessionData,
  index,
  enrolment,
  isPlanningSession,
  title,
  typeLabel,
  coinTotal,
  coinEarned = 0,
  thumbnailUrl = null,
  primaryAction = null,
  deleteAction = null,
  menuItems = [],
  subtitle = "",
}: PlanningCompactMetaCardProps) => {
  const controller = usePlanLessonLocal()

  const checkbox = getCheckboxProps({
    controller,
    isPlanningSession,
    sessionData,
    activity,
    enrolment,
  })

  const statusMeta = getStatusMeta(activity?.status)
  const scoreText = getScoreText(sessionData, activity)
  const highlightClass = getHighlightClass(sessionData, activity, {
    isGeneral: true,
    activityFlowCompleted: Number(activity?.status) === 1,
    isPlatform: false,
  })
  const rolledOverText = getRolledOverText(activity?.roll_date)

  return (
    <div
      className={`${highlightClass} set1 bg-supermild`}
      data-key={activity.pk_lesson_activity_key}
      style={{
        minHeight: "unset",
        padding: "4px 6px",
      }}
    >
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {rolledOverText ? (
            <span
              className="cs-txt-danger me-2"
              style={{ fontSize: "8px", display: "block", lineHeight: 1.1 }}
            >
              {rolledOverText}
            </span>
          ) : null}
          <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
            {statusMeta.label ? (
              <span
                className={`${statusMeta.className}`}
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {statusMeta.label}
              </span>
            ) : null}

            {scoreText ? (
              <span
                className="text-danger"
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {scoreText}
              </span>
            ) : null}

            {activity?.tutor_comments ? (
              <span
                className="text-dark"
                style={{ cursor: "pointer", fontSize: "9px", lineHeight: 1 }}
                title={`Comment: ${activity.tutor_comments}`}
              >
                <i className="fas fa-info-circle"></i>
              </span>
            ) : null}

            {checkbox?.checkable ? (
              <input
                type="checkbox"
                checked={!!checkbox.checked}
                onMouseDown={event => event.stopPropagation()}
                onChange={event => {
                  event.stopPropagation()
                  void checkbox.onCheckedChanged?.()
                }}
              />
            ) : null}
          </div>

          <div className="d-flex align-items-start gap-2">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <h6
                className="text-dark fw-700"
                style={{
                  fontSize: "10px",
                  lineHeight: 1.15,
                  marginBottom: "3px",
                  overflow: "hidden",
                  wordBreak: "break-word",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                }}
                title={title}
              >
                {index + 1}) {title}
              </h6>
              {subtitle ? (
                <div
                  className="text-dark fw-400"
                  style={{
                    fontSize: "9px",
                    lineHeight: 1.2,
                    marginBottom: "3px",
                    overflow: "hidden",
                    wordBreak: "break-word",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                  title={subtitle}
                >
                  {subtitle}
                </div>
              ) : null}
            </div>

            <CompactMetaCardThumbnail thumbnailUrl={thumbnailUrl} size={44} />
          </div>

          <div
            className="border-top"
            style={{ marginTop: "2px", marginBottom: "3px", opacity: 0.7 }}
          ></div>

          <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
            <div
              className="d-flex align-items-center fw-400 text-dark"
              style={{ fontSize: "9px", lineHeight: 1.05 }}
            >
              <span>
                <img className="me-05" width={12} src={reel} alt="" />
              </span>
              {typeLabel}
            </div>

            <div
              className="d-flex align-items-center fw-400 text-dark"
              style={{ fontSize: "9px", lineHeight: 1.05 }}
            >
              <img className="me-05" width={12} src={awicon} alt="" />
              {coinEarned}/{coinTotal}
            </div>
          </div>

          <div style={{ fontSize: "9px" }}>&nbsp;</div>
        </div>

        <div
          style={{
            width: `${COMPACT_CARD_ACTION_WIDTH}px`,
            flex: `0 0 ${COMPACT_CARD_ACTION_WIDTH}px`,
          }}
        >
          <div className="d-grid" style={{ gap: "3px" }}>
            {primaryAction ? (
              <PlanningCardActionButton
                label={primaryAction.label}
                onClick={primaryAction.onClick}
                tone={primaryAction.tone}
                disabled={!!primaryAction.disabled}
              />
            ) : (
              <EmptyPlanningActionCell />
            )}

            <div
              className="d-grid"
              style={{
                gridTemplateColumns: "1fr 1fr",
                gap: "3px",
              }}
            >
              <div className="d-grid" style={{ gap: "3px" }}>
                {deleteAction ? (
                  <PlanningCardActionButton
                    label="Delete"
                    onClick={deleteAction}
                    tone="delete"
                  />
                ) : (
                  <EmptyPlanningActionCell />
                )}

                {menuItems.length > 0 ? (
                  <PlanningCardActionMenu items={menuItems} />
                ) : (
                  <EmptyPlanningActionCell />
                )}
              </div>

              <div className="d-grid" style={{ gap: "3px" }}>
                <EmptyPlanningActionCell />
                <EmptyPlanningActionCell />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const SessionCompactMetaCard = ({
  activity,
  sessionData,
  index,
  enrolment,
  isPlanningSession,
  title,
  typeLabel,
  coinTotal,
  coinEarned = 0,
  thumbnailUrl = null,
  rolloverActionsEnabled = false,
  onRolloverToNextLesson = null,
  onRolloverToHomework = null,
  onRolloverToNextSession = null,
  menuItems = [],
  showNextAvailable = false,
  subtitle = "",
}: SessionCompactMetaCardProps) => {
  const controller = usePlanLessonLocal()

  const checkbox = getCheckboxProps({
    controller,
    isPlanningSession,
    sessionData,
    activity,
    enrolment,
  })

  const statusMeta = getStatusMeta(activity?.status)
  const scoreText = getScoreText(sessionData, activity)
  const highlightClass = getHighlightClass(sessionData, activity, {
    isGeneral: true,
    activityFlowCompleted: Number(activity?.status) === 1,
    isPlatform: false,
  })
  const rolledOverText = getRolledOverText(activity?.roll_date)

  const sessionMenuItems: PlanningActionMenuItem[] = [
    ...(rolloverActionsEnabled && onRolloverToNextSession
      ? [
          {
            key: "rollOverToNextSession",
            label: "Roll over to next session",
            icon: "mdi mdi-skip-forward",
            onClick: onRolloverToNextSession,
          },
        ]
      : []),
    ...menuItems,
  ]

  return (
    <div
      className={`${highlightClass} set1 bg-supermild`}
      data-key={activity.pk_lesson_activity_key}
      style={{
        minHeight: "unset",
        padding: "4px 6px",
      }}
    >
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {rolledOverText ? (
            <span
              className="cs-txt-danger me-2"
              style={{ fontSize: "8px", display: "block", lineHeight: 1.1 }}
            >
              {rolledOverText}
            </span>
          ) : null}
          <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
            <div
              className="d-flex align-items-center fw-400 text-dark"
              style={{ fontSize: "8px", lineHeight: 1.1 }}
            >
              <span>
                <img className="me-05" width={12} src={reel} alt="" />
              </span>
              {typeLabel}
            </div>

            {showNextAvailable ? (
              <span
                style={{
                  borderRadius: "5px",
                  backgroundColor: "#5b51ff",
                  color: "#ffffff",
                  padding: "1px 5px",
                  fontSize: "8px",
                  lineHeight: 1.1,
                }}
              >
                next available
              </span>
            ) : null}

            {statusMeta.label ? (
              <span
                className={`${statusMeta.className}`}
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {statusMeta.label}
              </span>
            ) : null}

            {scoreText ? (
              <span
                className="text-danger"
                style={{ fontSize: "8px", lineHeight: 1 }}
              >
                {scoreText}
              </span>
            ) : null}

            {activity?.tutor_comments ? (
              <span
                className="text-dark"
                style={{ cursor: "pointer", fontSize: "8px", lineHeight: 1 }}
                title={`Comment: ${activity.tutor_comments}`}
              >
                <i className="fas fa-info-circle"></i>
              </span>
            ) : null}

            {checkbox?.checkable ? (
              <input
                type="checkbox"
                checked={!!checkbox.checked}
                onMouseDown={event => event.stopPropagation()}
                onChange={event => {
                  event.stopPropagation()
                  void checkbox.onCheckedChanged?.()
                }}
              />
            ) : null}
          </div>

          <div className="d-flex justify-content-between align-items-start gap-2">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div className="d-flex align-items-start gap-2">
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <h6
                    className="text-dark fw-700"
                    style={{
                      fontSize: "10px",
                      lineHeight: 1.2,
                      marginBottom: "3px",
                      overflow: "hidden",
                      wordBreak: "break-word",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                    title={title}
                  >
                    {index + 1}) {title}
                  </h6>
                  {subtitle ? (
                    <div
                      className="text-dark fw-400"
                      style={{
                        fontSize: "9px",
                        lineHeight: 1.2,
                        marginBottom: "3px",
                        overflow: "hidden",
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                      title={subtitle}
                    >
                      {subtitle}
                    </div>
                  ) : null}
                </div>

                <CompactMetaCardThumbnail
                  thumbnailUrl={thumbnailUrl}
                  size={44}
                />
              </div>

              <div className="d-flex justify-content-end align-items-center flex-wrap gap-1 mb-1">
                <div
                  className="d-flex align-items-center fw-400 text-dark"
                  style={{ fontSize: "9px", lineHeight: 1.05 }}
                >
                  <img className="me-05" width={12} src={awicon} alt="" />
                  {coinEarned}/{coinTotal}
                </div>
              </div>
            </div>

            <div
              style={{
                width: `${SESSION_CARD_ACTION_WIDTH}px`,
                flex: `0 0 ${SESSION_CARD_ACTION_WIDTH}px`,
              }}
            >
              <div className="d-grid" style={{ gap: "3px" }}>
                {rolloverActionsEnabled &&
                (onRolloverToNextLesson || onRolloverToHomework) ? (
                  <div
                    className="d-grid"
                    style={{ gridTemplateColumns: "1fr 1fr", gap: "3px" }}
                  >
                    {onRolloverToNextLesson ? (
                      <PlanningCardActionButton
                        label={
                          <>
                            <i
                              className="mdi mdi-redo"
                              style={{ fontSize: "10px", marginRight: "2px" }}
                              aria-hidden="true"
                            />
                            To lesson
                          </>
                        }
                        onClick={onRolloverToNextLesson}
                        tone="secondary"
                        fontSize={8}
                        title="Rollover to next Lesson"
                      />
                    ) : (
                      <EmptyPlanningActionCell />
                    )}
                    {onRolloverToHomework ? (
                      <PlanningCardActionButton
                        label={
                          <>
                            <i
                              className="mdi mdi-redo"
                              style={{ fontSize: "10px", marginRight: "2px" }}
                              aria-hidden="true"
                            />
                            To h/w
                          </>
                        }
                        onClick={onRolloverToHomework}
                        tone="secondary"
                        fontSize={8}
                        title="Rollover to Homework"
                      />
                    ) : (
                      <EmptyPlanningActionCell />
                    )}
                  </div>
                ) : null}

                {sessionMenuItems.length > 0 ? (
                  <PlanningCardActionMenu items={sessionMenuItems} fontSize={9} />
                ) : (
                  <EmptyPlanningActionCell />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const LocalMathOnlineCard = ({
  isHomework,
  isPlanningSession,
  activity,
  sessionData,
  index,
  enrolment,
}: BasePlanningCardProps) => {
  const controller = usePlanLessonLocal()

  const copyToHomework = createCopyToHomeworkHandler(
    controller,
    activity,
    isPlanningSession
  )

  const sharedActions = createSharedCardActions(controller, {
    isHomework,
    isPlanningSession,
    activity,
    sessionData,
    copyToHomework,
    restrictDeleteToTutorOwner: true,
  })

  const assessmentActions = createAssessmentActions(
    controller,
    activity,
    isPlanningSession
  )

  if (isPlanningSession) {
    return (
      <PlanningCompactActivityCard
        isHomework={isHomework}
        isPlanningSession={isPlanningSession}
        activity={activity}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
        typeLabel="Online Activities"
        sharedActions={sharedActions}
        assessmentActions={assessmentActions}
      />
    )
  }

  return (
    <SessionCompactActivityCard
      isHomework={isHomework}
      isPlanningSession={isPlanningSession}
      activity={activity}
      sessionData={sessionData}
      index={index}
      enrolment={enrolment}
      typeLabel="Online Activities"
      sharedActions={sharedActions}
      assessmentActions={assessmentActions}
    />
  )
}

export const LocalEnglishOnlineCard = ({
  isHomework,
  isPlanningSession,
  activity,
  sessionData,
  index,
  enrolment,
}: BasePlanningCardProps) => {
  const controller = usePlanLessonLocal()

  const copyToHomework = createCopyToHomeworkHandler(
    controller,
    activity,
    isPlanningSession
  )

  const sharedActions = createSharedCardActions(controller, {
    isHomework,
    isPlanningSession,
    activity,
    sessionData,
    copyToHomework,
    restrictDeleteToTutorOwner: true,
  })

  const assessmentActions = createAssessmentActions(
    controller,
    activity,
    isPlanningSession
  )

  let englishManualMark = 0
  let englishManualMarkReview = 0
  if (Array.isArray(activity?.LessonActivitiesQuestions)) {
    activity.LessonActivitiesQuestions.forEach((activityQuestion: any) => {
      englishManualMarkReview += Number(
        activityQuestion?.marked_for_manual_marking || 0
      )
      activityQuestion?.english_question?.forEach((question: any) => {
        question?.english_question_parts?.forEach((questionpart: any) => {
          const categoryId = question?.english_question_category_id
          if (categoryId === 10 || categoryId === 13) {
            const hasAnswer =
              questionpart?.english_question_answers?.length > 0 &&
              questionpart.english_question_answers[0]?.english_answer_text !==
                ""
            if (
              hasAnswer &&
              questionpart?.english_question_part_automark === 0
            ) {
              englishManualMark += 1
            }
          } else if (questionpart?.english_question_part_automark === 0) {
            englishManualMark += 1
          }
        })
      })
    })
  }
  const manualMarkingRequired =
    englishManualMark > 0 && englishManualMarkReview === 0

  if (isPlanningSession) {
    return (
      <PlanningCompactActivityCard
        isHomework={isHomework}
        isPlanningSession={isPlanningSession}
        activity={activity}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
        typeLabel="Online Activities"
        sharedActions={sharedActions}
        assessmentActions={assessmentActions}
        manualMarkingRequired={manualMarkingRequired}
      />
    )
  }

  return (
    <SessionCompactActivityCard
      isHomework={isHomework}
      isPlanningSession={isPlanningSession}
      activity={activity}
      sessionData={sessionData}
      index={index}
      enrolment={enrolment}
      typeLabel="Online Activities"
      sharedActions={sharedActions}
      assessmentActions={assessmentActions}
      manualMarkingRequired={manualMarkingRequired}
    />
  )
}

export const LocalGeneralActivityCard = ({
  isHomework,
  isPlanningSession,
  activity,
  sessionData,
  index,
  enrolment,
}: BasePlanningCardProps) => {
  const controller = usePlanLessonLocal()

  const copyToHomework = createCopyToHomeworkHandler(
    controller,
    activity,
    isPlanningSession
  )

  const editableActivityTypes: number[] = [
    ...MY_LIBRARY_ACTIVITY_TYPES,
    ActivityType.ONETIME,
  ]
  const activityType = getActivityType(activity)

  const sharedActions = createSharedCardActions(controller, {
    isHomework,
    isPlanningSession,
    activity,
    sessionData,
    copyToHomework,
  })

  const assessmentActions = createAssessmentActions(
    controller,
    activity,
    isPlanningSession
  )

  const editAction = {
    enabled: editableActivityTypes.includes(activityType) && isPlanningSession,
    onActivate: createGeneralEditHandler(controller, activity, isHomework),
  }

  const typeLabel =
    ActivityType.ONETIME === activityType
      ? "Task"
      : activity?.type || "Activity"

  const isManualMarkType =
    activityType === ActivityType.MY_LIBRARY_WORKSHEET ||
    activityType === ActivityType.PLATFORM_WORKSHEET
  let generalManualMarkReview = 0
  if (isManualMarkType && Array.isArray(activity?.LessonActivitiesQuestions)) {
    activity.LessonActivitiesQuestions.forEach((question: any) => {
      generalManualMarkReview += Number(
        question?.marked_for_manual_marking || 0
      )
    })
  }
  const manualMarkingRequired =
    isManualMarkType && generalManualMarkReview === 0

  const isPlatformActivity =
    activityType === ActivityType.PLATFORM_VIDEO ||
    activityType === ActivityType.PLATFORM_WORKSHEET
  const activityFlowCompleted = isManualMarkType
    ? Number(activity?.status) === 1 && generalManualMarkReview > 0
    : Number(activity?.status) === 1
  const highlightClassOverride = getHighlightClass(sessionData, activity, {
    isGeneral: true,
    activityFlowCompleted,
    isPlatform: isPlatformActivity,
  })

  if (isPlanningSession) {
    return (
      <PlanningCompactActivityCard
        isHomework={isHomework}
        isPlanningSession={isPlanningSession}
        activity={activity}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
        typeLabel={typeLabel}
        sharedActions={sharedActions}
        assessmentActions={assessmentActions}
        editAction={editAction}
        manualMarkingRequired={manualMarkingRequired}
        highlightClassOverride={highlightClassOverride}
      />
    )
  }

  const markAction = {
    enabled: !isPlanningSession,
    onActivate: () => controller.onManualMarkingActivity(activity as any),
  }

  return (
    <SessionCompactActivityCard
      isHomework={isHomework}
      isPlanningSession={isPlanningSession}
      activity={activity}
      sessionData={sessionData}
      index={index}
      enrolment={enrolment}
      typeLabel={typeLabel}
      sharedActions={sharedActions}
      assessmentActions={assessmentActions}
      markAction={markAction}
      manualMarkingRequired={manualMarkingRequired}
      highlightClassOverride={highlightClassOverride}
    />
  )
}

export const LocalLllActivityCard = ({
  isPlanningSession,
  activity,
  sessionData,
  index,
  enrolment,
}: SessionOnlyCardProps) => {
  const controller = usePlanLessonLocal()
  const { title: lllTopicTitle, subtitle: lllActivitySubtitle } =
    useResolvedLllTitle(activity)
  const lllDisplayTitle = `LLL: ${lllTopicTitle}`

  const showDelete = isPlanningSession
  const showRollover =
    !isPlanningSession && isSessionCompleted(sessionData?.session || undefined)

  const hasNextLllActivity = (() => {
    try {
      const lllNode = lllLookupService.findNodeByFqn(
        activity?.activity_ref_id as string
      )
      if (!lllNode) return false
      const searcher = new LllNextActivitySearcher()
      return !!searcher.getNextActivity(lllNode)
    } catch {
      return false
    }
  })()

  const addNextActivity = async (isHomework: boolean) => {
    const lllNode = lllLookupService.findNodeByFqn(
      activity.activity_ref_id as string
    )
    const searcher = new LllNextActivitySearcher()
    const nextActivityNode = searcher.getNextActivity(lllNode)

    if (!nextActivityNode) {
      toast.error("No next activity available", { autoClose: 3000 })
      return
    }

    const activityList = isHomework
      ? controller.homeworkActivityArray
      : controller.lessonActivityArray

    const alreadyAdded = !!activityList.find(
      item => item?.activity_ref_id === nextActivityNode.fqn
    )

    if (alreadyAdded) {
      toast.error("Next activity already added", { autoClose: 3000 })
      return
    }

    const selectedActivity = createLllSelectedActivity(
      activity,
      nextActivityNode,
      isHomework
    )

    const originalActivityType = localStorage.getItem("typeAct")
    localStorage.setItem("selectedData", JSON.stringify([selectedActivity]))

    try {
      controller.setActivityIsHW(isHomework ? 1 : 0)
      localStorage.setItem("typeAct", isHomework ? "0" : "1")
      await controller.OnSubmitActivityDataAuto()
    } catch (error) {
      toast.error("Failed to add next activity", { autoClose: 3000 })
    } finally {
      localStorage.removeItem("selectedData")

      if (originalActivityType !== null) {
        localStorage.setItem("typeAct", originalActivityType)
      } else {
        localStorage.removeItem("typeAct")
      }
    }
  }

  if (isPlanningSession) {
    return (
      <PlanningCompactMetaCard
        activity={activity}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
        isPlanningSession={isPlanningSession}
        title={lllDisplayTitle}
        subtitle={lllActivitySubtitle}
        typeLabel="LLL"
        coinTotal={LllMetadata.coinsPerActivity}
        coinEarned={getCoinEarned(activity)}
        deleteAction={() =>
          runAsyncAction(() =>
            controller.onDelectActivity(activity.pk_lesson_activity_key)
          )
        }
      />
    )
  }

  const menuItems: PlanningActionMenuItem[] = []

  menuItems.push({
    key: "addNextActivity",
    label: "Add the next activity",
    icon: "mdi mdi-skip-next",
    onClick: () => addNextActivity(false),
  })

  menuItems.push({
    key: "addNextActivityToHomework",
    label: "Add the next activity to homework",
    icon: "mdi mdi-skip-next",
    onClick: () => addNextActivity(true),
  })

  return (
    <SessionCompactMetaCard
      activity={activity}
      sessionData={sessionData}
      index={index}
      enrolment={enrolment}
      isPlanningSession={isPlanningSession}
      title={lllDisplayTitle}
      subtitle={lllActivitySubtitle}
      typeLabel="LLL"
      coinTotal={LllMetadata.coinsPerActivity}
      coinEarned={getCoinEarned(activity)}
      onRolloverToNextLesson={
        showRollover
          ? () => controller.onRolloverActivityToNextSession(activity as any, 0)
          : null
      }
      onRolloverToHomework={
        showRollover
          ? () => controller.onRolloverActivityToNextSession(activity as any, 1)
          : null
      }
      onRolloverToNextSession={
        showRollover
          ? () => controller.onMoveActivityPopup(activity as any)
          : null
      }
      rolloverActionsEnabled={showRollover}
      menuItems={menuItems}
      showNextAvailable={!isPlanningSession && hasNextLllActivity}
    />
  )
}

export const LocalGameCard = ({
  isPlanningSession,
  activity,
  sessionData,
  index,
  enrolment,
}: SessionOnlyCardProps) => {
  const controller = usePlanLessonLocal()
  const {
    title: gameName,
    thumbnailUrl: gameThumbnailUrl,
    description: gameDescription,
  } = useResolvedGameDetails(activity)
  const gameDisplayTitle = `Game: ${gameName}`
  const showRollover =
    !isPlanningSession && isSessionCompleted(sessionData?.session || undefined)
  const gamePreviewEnabled = Number(activity?.status) === 1

  const previewGame = () => {
    window.location.assign(`/game-summary/${activity.pk_lesson_activity_key}`)
  }

  if (isPlanningSession) {
    return (
      <PlanningCompactMetaCard
        activity={activity}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
        isPlanningSession={isPlanningSession}
        title={gameDisplayTitle}
        subtitle={gameDescription}
        typeLabel="Game"
        coinTotal={GameMetadata.coinsPerGame}
        coinEarned={getCoinEarned(activity)}
        thumbnailUrl={gameThumbnailUrl}
        primaryAction={{
          label: "Preview",
          onClick: previewGame,
          tone: "preview",
          disabled: !gamePreviewEnabled,
        }}
        deleteAction={() =>
          runAsyncAction(() =>
            controller.onDelectActivity(activity.pk_lesson_activity_key)
          )
        }
      />
    )
  }

  const menuItems: PlanningActionMenuItem[] = [
    {
      key: "preview",
      label: gamePreviewEnabled ? "Preview" : "Preview (complete first)",
      icon: "mdi mdi-eye-outline",
      onClick: () => {
        if (!gamePreviewEnabled) {
          return
        }

        previewGame()
      },
    },
  ]

  return (
    <SessionCompactMetaCard
      activity={activity}
      sessionData={sessionData}
      index={index}
      enrolment={enrolment}
      isPlanningSession={isPlanningSession}
      title={gameDisplayTitle}
      subtitle={gameDescription}
      typeLabel="Game"
      coinTotal={GameMetadata.coinsPerGame}
      coinEarned={getCoinEarned(activity)}
      thumbnailUrl={gameThumbnailUrl}
      onRolloverToNextLesson={
        showRollover
          ? () => controller.onRolloverActivityToNextSession(activity as any, 0)
          : null
      }
      onRolloverToHomework={
        showRollover
          ? () => controller.onRolloverActivityToNextSession(activity as any, 1)
          : null
      }
      onRolloverToNextSession={
        showRollover
          ? () => controller.onMoveActivityPopup(activity as any)
          : null
      }
      rolloverActionsEnabled={showRollover}
      menuItems={menuItems}
    />
  )
}

export const DraggableCard = ({
  children,
  activity,
  disabled,
  activityIndex,
  isHomework,
}: DraggableCardProps) => {
  const controller = usePlanLessonLocal()
  const className = getCardClassName(controller, activityIndex, isHomework)
  const draggableId = getDraggableActivityId(activity)

  const handleClick = () => {
    if (isHomework) {
      controller.setActivityNavHomeSeleceted(activityIndex)
      return
    }

    controller.setActivityNavSeleceted(activityIndex)
  }

  return (
    <Draggable
      isDragDisabled={!!disabled}
      key={draggableId}
      draggableId={draggableId}
      index={activityIndex}
    >
      {(provided: any) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={handleClick}
          className={className}
          style={{
            ...provided.draggableProps.style,
            paddingBottom: "10px",
          }}
        >
          {children}
        </div>
      )}
    </Draggable>
  )
}
